import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { StreamChat } from "stream-chat";
import { StreamVideoClient } from "@stream-io/video-react-sdk";
import {
  streamTokenService,
  chatCallOutcomeService,
  chatActionService,
} from "../../services/api";
import { stripMessagingPrefix } from "../../utils/chatTimer";
import { store } from "../../store";
import { setCredentials } from "../../store/slices/authSlice";
import { classifyChannelLane, LANE } from "../../utils/laneMapping";
import { computeInboxModel, channelPerspective } from "../../utils/inboxRoles";
import { deriveChannelIntent } from "../../utils/chatIntent";
import { isExpiredChannel, shouldAutoReturn } from "../../components/inbox/archiveState";
import { channelUnreadCount } from "../../utils/chatUnread";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

// D29: a cold-contact message-request files under the lane of its ORIGIN
// (direct → DM, last_minute → Last Minute, agent_requests → Agent Req.). Club
// Req. / Trusted Agent / Scout Req. never host agent↔agent message requests.
export const MESSAGE_REQUEST_ORIGIN_TO_LANE = {
  direct: LANE.DM,
  last_minute: LANE.LAST_MINUTE,
  agent_requests: LANE.AGENT_REQ,
};

const apiKey = process.env.REACT_APP_STREAM_CHAT_API_KEY;

// T031 (symptom 3): every inbox channel query sorts by Stream's composite
// `last_updated` (= last message date, or the channel creation date when the
// channel has no messages) instead of bare `last_message_at`. A channel whose
// first message was never sent has last_message_at = null, so under the old
// sort it landed at the very END of the server query — past the 20-row first
// page — and an empty chat simply vanished from the list on reload. With
// `last_updated` it slots in by creation time; channels that DO have messages
// keep their existing most-recent-first order.
export const INBOX_CHANNEL_SORT = [{ last_updated: -1 }];

// classifyInboxChannel — route a Stream channel to exactly one canonical inbox
// LANE (Design Bible D3/D4/D25). Delegates to the shared, viewer-relative
// laneMapping module; `perspective` is agent by default (the coarse callers —
// preview "is this DM?", search bucketing — pass the viewer's real perspective
// where they know it). The full origin→lane logic and its fallback chain live in
// utils/laneMapping.js so there is ONE classifier, not a per-call heuristic.
export const classifyInboxChannel = (channel, perspective) =>
  classifyChannelLane(channel, perspective);

// counterpartType — classify a channel's counterpart into one of the 4b search
// scopes (agents / clubs / staff / players) using only channel.data, mirroring
// CustomChannelPreview's agent/club heuristic. Agents and clubs are the real
// counterpart types in these threads; staff/players are valid scopes that rarely
// match (few such counterparts exist), so they honestly narrow to near-empty
// rather than pretending. Returns "other" when nothing is stampable.
export const counterpartType = (channel, selfId) => {
  const users = Array.isArray(channel?.data?.users) ? channel.data.users : [];
  const other = users.find((u) => String(u?.id) !== String(selfId)) || {};
  const role = String(other.role || "").toLowerCase();
  const email = String(other.email || "").toLowerCase();

  const isAgent =
    Boolean(other.agency_name) ||
    Boolean(other.agency_id) ||
    role.includes("agent") ||
    email.includes("agent");
  if (isAgent) return "agents";
  if (role.includes("player")) return "players";
  const isClub =
    Boolean(other.club_name) ||
    other.club_id != null ||
    role.includes("club") ||
    role.includes("scout") ||
    role.includes("director") ||
    email.includes("club");
  if (isClub) return "clubs";
  if (role.includes("staff")) return "staff";
  return "other";
};

export const useInbox = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const roles = useSelector((state) => state.auth.roles);
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — route it through the same static-bundle lookup the
  // scanner uses before it goes to the native dialog.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;

  // Perspective-specific inbox model (D1/D20): the role-scoped tab set, whether a
  // tab bar renders at all (DM-only roles hide it), the default lane, and the
  // agent/club capability flags laneMapping needs for viewer-relative filing.
  const inboxModel = useMemo(
    () => computeInboxModel({ roles, user }),
    [roles, user],
  );
  const { laneKeys, defaultLane, agentCapable, clubCapable } = inboxModel;

  const [chatClient, setChatClient] = useState(null);
  const [isChatReady, setIsChatReady] = useState(false);
  const [activeChannel, setActiveChannel] = useState(null);
  const [initialChannelSet, setInitialChannelSet] = useState(false);

  // Tabbed inbox. `activeTab` drives the client-side channel render filter;
  // `tabCounts` holds per-lane unread aggregation for the count numbers.
  const [activeTab, setActiveTab] = useState(defaultLane);
  const [tabCounts, setTabCounts] = useState({});
  // Per-tab TOTAL (not just unread) channel counts — the authoritative signal for
  // the per-tab empty state (#23): Stream's ChannelList EmptyStateIndicator only
  // fires when the server query is empty, never when a tab's client render-filter
  // narrows to zero while other tabs still hold channels.
  const [tabTotals, setTabTotals] = useState({});
  // D24: per-lane archived counts, split by species: { [lane]: {manual, expired} }.
  const [archivedCounts, setArchivedCounts] = useState({});
  // D24: bumped after a manual archive/unarchive so the ChannelLists re-query and
  // the row moves between the main list and the archive without a page reload.
  const [listRefreshKey, setListRefreshKey] = useState(0);
  // D24 auto-return (client mirror of the backend webhook): the cids the viewer
  // archived MANUALLY this session. A message.new on one of them pulls it back.
  const manualArchivedCidsRef = useRef(new Set());
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  // D29: pending cold-contact request counts keyed by the LANE they file under
  // (via their origin). Feeds the per-lane tab numbers; the DM/LM/Agent-Req.
  // lanes host the message-request gate, the others never do.
  const [pendingRequestsByLane, setPendingRequestsByLane] = useState({});
  // D11: DM-tab intent filter + DM-scoped per-intent counts. Only bites while
  // the DM lane is active; every other lane ignores it (the tab is the filter).
  const [intentFilter, setIntentFilter] = useState("all");
  const [dmIntentCounts, setDmIntentCounts] = useState({});

  // Keep the active lane valid as the role model resolves (roles can load after
  // first paint) or the user switches role: if the current tab isn't in this
  // role's lane set, fall back to the default lane.
  useEffect(() => {
    if (!laneKeys.includes(activeTab)) setActiveTab(defaultLane);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneKeys, defaultLane]);

  const [sidebarSearchQuery, setSidebarSearchQuery] = useState("");
  // 4b — counterpart scope for inbox search (all / clubs / agents / staff /
  // players). Only bites while a query is present; the resting list is untouched.
  const [searchScope, setSearchScope] = useState("all");

  const [videoClient, setVideoClient] = useState(null);
  const [call, setCall] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  const [callType, setCallType] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showIncomingCallModal, setShowIncomingCallModal] = useState(false);
  const [tokensRefreshed, setTokensRefreshed] = useState(false);

  // ── Call-outcome stamping (3e cards, #7-FE) ──────────────────────────────
  // The CALLER's client owns the terminal stamp of an OUTGOING call: on end it
  // posts `outgoing` (+ connection duration) if a second participant ever
  // joined, else `missed`. `callMetaRef` carries the sent call_invitation's
  // {call_id, message_id, channel_cid} up from the header's send path (via
  // handleStartCall's meta arg — CustomMessage can't reach it). `connectedRef`
  // flips true the first time participantCount hits 2; `connectedAtRef` marks
  // when, so the duration is the real connected span. All best-effort — a stamp
  // failure never touches the call flow.
  const callMetaRef = useRef(null);
  const callConnectedRef = useRef(false);
  const callConnectedAtRef = useRef(null);
  const callParticipantSubRef = useRef(null);

  const userId = user?.id?.toString();

  // laneOf — classify a channel to its canonical lane FROM THE VIEWER'S
  // perspective (D3/D4/D25). Agent-request / search-request threads file
  // differently for the agent vs the club side, so the perspective is resolved
  // per channel from the viewer's capabilities + own member stamp.
  const laneOf = useCallback(
    (channel) => {
      const data = channel?.data || channel || {};
      const perspective = channelPerspective(
        data,
        { agentCapable, clubCapable },
        userId,
      );
      return classifyChannelLane(channel, perspective);
    },
    [agentCapable, clubCapable, userId],
  );

  // Build the best display name we can from the Redux user object so the
  // Stream Chat record we register is named (not just numeric). Falls
  // through: explicit `name` → first+last → email local-part → userId.
  // This is what other peers see as `message.user.name` when this user
  // sends a chat / call invitation.
  const userName = (() => {
    if (user?.name && String(user.name).trim()) return String(user.name).trim();
    const composed = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (composed) return composed;
    if (user?.email) return String(user.email).split("@")[0];
    return userId;
  })();

  // Refresh Stream tokens and initialise video client
  useEffect(() => {
    if (!userId || !userName) return;

    let cancelled = false;
    let createdVideoClient = null;

    const initializeVideoClient = async () => {
      try {
        const refreshResult = await streamTokenService.refreshStreamToken();

        if (!refreshResult.success) {
          console.error(
            "Failed to refresh Stream tokens:",
            refreshResult.error
          );
          return;
        }

        if (cancelled) {
          return;
        }

        const freshStreamToken = refreshResult.token;

        const currentUser = store.getState().auth.user;
        const updatedUser = {
          ...currentUser,
          streamChatId: freshStreamToken,
          steamVideoId: freshStreamToken,
        };

        localStorage.setItem("user", JSON.stringify(updatedUser));

        store.dispatch(
          setCredentials({
            user: updatedUser,
            tokens: store.getState().auth.tokens,
          })
        );

        createdVideoClient = new StreamVideoClient({
          apiKey: process.env.REACT_APP_STREAM_CHAT_API_KEY,
          user: {
            id: userId,
            name: userName,
          },
          token: freshStreamToken,
        });

        if (cancelled) {
          try {
            await createdVideoClient.disconnectUser();
          } catch (disconnectError) {
            console.error(
              "Error disconnecting cancelled video client:",
              disconnectError
            );
          }
          return;
        }

        setVideoClient(createdVideoClient);
        setTokensRefreshed(true);
      } catch (error) {
        console.error("Error initializing video client:", error);
      }
    };

    initializeVideoClient();

    return () => {
      cancelled = true;
      if (createdVideoClient) {
        createdVideoClient.disconnectUser().catch((error) => {
          console.error("Error disconnecting video client:", error);
        });
      }
    };
  }, [userId, userName]);

  // Handle incoming call invitation or video call object
  const handleIncomingCall = useCallback(
    async (incomingCallData) => {
      if (!incomingCallData) return;

      try {
        if (incomingCallData.isInvitation) {
          if (!videoClient) {
            console.warn("Video client is not ready for incoming call");
            return;
          }

          const callInstance = videoClient.call(
            "default",
            incomingCallData.id
          );

          await callInstance.join({ create: false });

          setCall(callInstance);
          setCallType(incomingCallData.type);
          setIsInCall(true);
        } else {
          await incomingCallData.join();
          setCall(incomingCallData);
          setCallType(incomingCallData.type);
          setIsInCall(true);
        }
      } catch (error) {
        console.error("Error joining incoming call:", error);
        alert(tr("Failed to join the call. The call may have ended."));
      }
    },
    [videoClient]
  );

  const handleStartCall = useCallback((newCall, type, meta) => {
    setCall(newCall);
    setCallType(type);
    setIsInCall(true);

    // Reset the outcome trackers for this outgoing call and remember the
    // call_invitation message so the end-of-call stamp can target it.
    callMetaRef.current = meta || null;
    callConnectedRef.current = false;
    callConnectedAtRef.current = null;
    if (callParticipantSubRef.current) {
      try {
        callParticipantSubRef.current.unsubscribe();
      } catch (e) {
        /* observable already torn down */
      }
      callParticipantSubRef.current = null;
    }

    // "Ever connected" = participantCount reached 2 (the callee joined). We
    // watch the SDK observable so the signal survives the callee leaving before
    // the caller ends. Guarded because the observable shape is SDK-owned.
    try {
      const sub = newCall?.state?.participantCount$?.subscribe?.((count) => {
        if (count >= 2 && !callConnectedRef.current) {
          callConnectedRef.current = true;
          callConnectedAtRef.current = Date.now();
        }
      });
      callParticipantSubRef.current = sub || null;
    } catch (e) {
      console.error("Could not subscribe to call participant count:", e);
    }
  }, []);

  const handleEndCall = useCallback(async () => {
    if (call) {
      // Stamp the outgoing call's outcome before we tear the call down. Only
      // the caller holds callMetaRef (the header set it on start), so the
      // callee's end path is a no-op here. Connected → outgoing + duration;
      // never connected (cancel / timeout) → missed. Best-effort: swallow all.
      const meta = callMetaRef.current;
      if (meta?.message_id && meta?.channel_cid) {
        const connected = callConnectedRef.current;
        const payload = {
          message_id: meta.message_id,
          channel_cid: meta.channel_cid,
          outcome: connected ? "outgoing" : "missed",
        };
        if (connected && callConnectedAtRef.current) {
          payload.duration_seconds = Math.max(
            0,
            Math.round((Date.now() - callConnectedAtRef.current) / 1000)
          );
        }
        Promise.resolve(chatCallOutcomeService.stampOutcome(payload)).catch(
          (e) => console.error("Call-outcome stamp failed:", e)
        );
      }
      if (callParticipantSubRef.current) {
        try {
          callParticipantSubRef.current.unsubscribe();
        } catch (e) {
          /* observable already torn down */
        }
        callParticipantSubRef.current = null;
      }
      callMetaRef.current = null;
      callConnectedRef.current = false;
      callConnectedAtRef.current = null;

      try {
        await call.leave();
      } catch (error) {
        console.error("Error leaving call:", error);
      }
      setCall(null);
      setCallType(null);
      setIsInCall(false);
    }
  }, [call]);

  // Connect Stream chat client with refreshed token
  useEffect(() => {
    if (!apiKey) {
      console.error("Stream Chat API key is missing");
      return;
    }

    const client = new StreamChat(apiKey);
    let didConnect = false;

    const currentUser = store.getState().auth.user;
    const freshUserToken = currentUser?.streamChatId;

    if (!tokensRefreshed) {
      return;
    }

    if (!userId || !freshUserToken) {
      console.log("Missing data for chat connection", {
        userId,
        hasToken: Boolean(freshUserToken),
      });
      return;
    }

    const connectUser = async () => {
      try {
        if (client.userID) {
          await client.disconnectUser();
        }

        await client.connectUser(
          {
            id: userId,
            name: userName,
            image:
              currentUser?.profile_picture_url ||
              currentUser?.profile_image_url ||
              currentUser?.image ||
              null,
          },
          freshUserToken
        );

        setIsChatReady(true);

        const handleConnectionChanged = (event) => {
          setIsChatReady(Boolean(event?.online));
        };

        client.on("connection.changed", handleConnectionChanged);

        client.on("message.new", (event) => {
          const message = event.message;

          if (!message?.attachments?.length) return;

          const invitation = message.attachments.find(
            (attachment) => attachment.type === "call_invitation"
          );

          if (invitation && message.user?.id !== client.userID) {
            // Pick the best display name we can. Reject values that look
            // like raw user IDs (purely numeric, or equal to caller_id /
            // message.user.id) — that's the "427" problem the incoming
            // popup was showing when the upstream Stream Chat record had
            // no real name set.
            const candidates = [
              invitation.caller_name,
              message.user?.name,
              message.user?.email
                ? String(message.user.email).split("@")[0]
                : null,
            ];
            const callerId = String(
              invitation.caller_id || message.user?.id || ""
            );
            const looksLikeId = (v) => {
              if (!v) return true;
              const s = String(v).trim();
              return !s || s === callerId || /^\d+$/.test(s);
            };
            const friendlyName =
              candidates.find((c) => !looksLikeId(c)) || "Incoming caller";

            setIncomingCall({
              id: invitation.call_id,
              type: invitation.call_type,
              caller_name: friendlyName,
              caller_id: invitation.caller_id,
              caller_image:
                invitation.caller_image || message.user?.image || null,
              // Keep the call_invitation message identity so a Decline can
              // stamp `declined` onto it (the decliner never sent the message,
              // so this is the only handle it has — #7-FE decline path).
              message_id: message.id || null,
              channel_cid: stripMessagingPrefix(message.cid || event.cid || null),
              isInvitation: true,
            });
            setShowIncomingCallModal(true);
          }
        });

        didConnect = true;
        setChatClient(client);
      } catch (error) {
        console.error("Error connecting Stream Chat client:", error);
        if (client.userID) {
          try {
            await client.disconnectUser();
          } catch (disconnectError) {
            console.error("Error during client disconnect:", disconnectError);
          }
        }
        setIsChatReady(false);
        setChatClient(null);
      }
    };

    connectUser();

    return () => {
      if (didConnect) {
        setIsChatReady(false);
        client.disconnectUser().catch((error) => {
          console.error("Error disconnecting chat client:", error);
        });
      }
    };
  }, [userId, userName, tokensRefreshed]);

  // Unified deep-link contract (P2-FE-2). Honor all three caller shapes plus a
  // `?tab=` selector — no caller-side changes needed:
  //   1. location.state.channelId (+ channelType)          — tender / pitch flows
  //   2. location.state.directChat.{channelId,channelType} — AgentPublicProfile
  //   3. ?channel=<id>                                      — club-network search
  //   4. ?tab=<key>  (e.g. notify redirect /inbox?tab=requests, D-011)
  useEffect(() => {
    if (!chatClient || !isChatReady || initialChannelSet) return;

    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const channelParam = params.get("channel");
    const stateChannelId =
      location.state?.channelId || location.state?.directChat?.channelId;
    const channelType =
      location.state?.channelType ||
      location.state?.directChat?.channelType ||
      "messaging";
    const channelId = stateChannelId || channelParam;

    if (!channelId && !tabParam) return;

    if (tabParam && laneKeys.includes(tabParam)) {
      setActiveTab(tabParam);
    }

    if (channelId) {
      const channel = chatClient.channel(channelType, channelId);
      // Watch to populate channel.data so we can route it to its lane; the
      // channel still opens if watch fails (classify then falls back to DM).
      channel
        .watch()
        .catch(() => {})
        .finally(() => {
          if (!tabParam) setActiveTab(laneOf(channel));
          setActiveChannel(channel);
        });
    }

    setInitialChannelSet(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [chatClient, isChatReady, location, initialChannelSet, navigate, laneKeys, laneOf]);

  // Open a thread by its channel cid and switch to its origin tab. Used by the
  // Requests panel's Accept transition (design 1g "Open →") and reusable for any
  // deep-link into a specific thread.
  const openThreadByCid = useCallback(
    async (cid) => {
      if (!chatClient || !cid) return;
      const raw = String(cid);
      const id = raw.includes(":") ? raw.split(":")[1] : raw;
      const channel = chatClient.channel("messaging", id);
      try {
        await channel.watch();
      } catch (e) {
        /* open anyway; classify degrades to DM */
      }
      setActiveTab(laneOf(channel));
      setActiveChannel(channel);
    },
    [chatClient, laneOf],
  );

  // D24 manual archive: move a chat into the viewer's own archive list (row
  // action, frame 5i/5k). Records the cid so a later message.new can auto-return
  // it (client mirror of the backend webhook), then bumps the list-refresh key so
  // the ChannelLists re-query and the row moves out of the main list.
  const archiveChat = useCallback(async (channel) => {
    const id = channel?.id || stripMessagingPrefix(channel?.cid || "");
    if (!id) return { success: false };
    const res = await chatActionService.archiveChat(id);
    if (res.success) {
      const cid = String(channel?.cid || `messaging:${id}`);
      manualArchivedCidsRef.current.add(cid);
      if (activeChannel && (activeChannel.id === id)) setActiveChannel(null);
      setListRefreshKey((k) => k + 1);
    }
    return res;
  }, [activeChannel]);

  // D24 manual unarchive: return a chat from the archive to the main list. An
  // EXPIRED chat answers 409 (read-only, revive-only) — surfaced to the caller
  // via res.expired so the UI shows the pointer instead of silently failing.
  const unarchiveChat = useCallback(async (channel) => {
    const id = channel?.id || stripMessagingPrefix(channel?.cid || "");
    if (!id) return { success: false };
    const res = await chatActionService.unarchiveChat(id);
    if (res.success) {
      manualArchivedCidsRef.current.delete(String(channel?.cid || `messaging:${id}`));
      setListRefreshKey((k) => k + 1);
    }
    return res;
  }, []);

  // D24 auto-return (client mirror): a message.new on a chat the viewer archived
  // manually this session pulls it back into the main list. Expired chats never
  // return (read-only); system/own messages are skipped (see shouldAutoReturn).
  useEffect(() => {
    if (!chatClient || !userId || !isChatReady) return undefined;
    const handler = (event) => {
      const cid = event?.cid || event?.channel?.cid;
      if (!cid) return;
      const channel =
        chatClient.activeChannels?.[cid] ||
        (event.channel_type && event.channel_id
          ? chatClient.channel(event.channel_type, event.channel_id)
          : null);
      const ok = shouldAutoReturn({
        channel: channel || { cid, data: event.channel },
        message: event.message,
        selfId: userId,
        isManuallyArchived: (c) => manualArchivedCidsRef.current.has(String(c)),
      });
      if (!ok) return;
      const id = stripMessagingPrefix(cid);
      manualArchivedCidsRef.current.delete(String(cid));
      Promise.resolve(chatActionService.unarchiveChat(id))
        .catch(() => {})
        .finally(() => setListRefreshKey((k) => k + 1));
    };
    const sub = chatClient.on("message.new", handler);
    return () => sub?.unsubscribe?.();
  }, [chatClient, userId, isChatReady]);

  // Per-tab unread pills (design 2a). One lightweight queryChannels (independent
  // of the ChannelList render query, so counts are accurate regardless of that
  // list's pagination), re-run throttled on the events that move unread state.
  useEffect(() => {
    if (!chatClient || !userId || !isChatReady) return;

    let cancelled = false;
    let timer = null;

    const recompute = async () => {
      try {
        const channels = await chatClient.queryChannels(
          { type: "messaging", members: { $in: [userId] }, archived: false },
          INBOX_CHANNEL_SORT,
          { limit: 100, state: true, watch: false },
        );
        if (cancelled) return;
        const counts = {};
        const totals = {};
        // D11: DM-scoped intent tallies for the chip counts.
        const intentCounts = { all: 0 };
        for (const ch of channels) {
          const key = laneOf(ch);
          // T035: same reader as the inbox row badge (utils/chatUnread) — the
          // per-member read state, not the suppressible `state.unreadCount`.
          // A tab pill that contradicted the row it counts would be a new bug.
          counts[key] = (counts[key] || 0) + channelUnreadCount(ch, userId);
          totals[key] = (totals[key] || 0) + 1;
          if (key === LANE.DM) {
            intentCounts.all += 1;
            const intent = deriveChannelIntent(ch?.data);
            if (intent) intentCounts[intent] = (intentCounts[intent] || 0) + 1;
          }
        }
        setTabCounts(counts);
        setTabTotals(totals);
        setDmIntentCounts(intentCounts);
      } catch (e) {
        /* counts are best-effort; the numbers simply won't render */
      }
    };

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(recompute, 800);
    };

    recompute();

    const events = [
      "message.new",
      "notification.message_new",
      "notification.mark_read",
      "message.read",
      "notification.added_to_channel",
      "channel.updated",
      "channel.hidden",
      "channel.visible",
    ];
    const subs = events.map((e) => chatClient.on(e, schedule));

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      subs.forEach((s) => s?.unsubscribe?.());
    };
  }, [chatClient, userId, isChatReady, laneOf, listRefreshKey]);

  // Per-tab archived count (design 1d archive header "N expired chats ·
  // read-only"). Best-effort, same shape as tabCounts: one throttled query of the
  // user's archived channels, bucketed by tab. If it fails the header simply
  // reports zero for the tab and the archive section stays hidden.
  useEffect(() => {
    if (!chatClient || !userId || !isChatReady) return;

    let cancelled = false;
    let timer = null;

    const recompute = async () => {
      try {
        const channels = await chatClient.queryChannels(
          { type: "messaging", members: { $in: [userId] }, archived: true },
          INBOX_CHANNEL_SORT,
          { limit: 100, state: true, watch: false },
        );
        if (cancelled) return;
        // D24: bucket per lane AND per species (manual vs timer-expired), so the
        // archive footer can read "N archived by you · M expired" and the two
        // groups render distinguishably (frame 5i).
        const counts = {};
        for (const ch of channels) {
          const key = laneOf(ch);
          const bucket = counts[key] || (counts[key] = { manual: 0, expired: 0 });
          if (isExpiredChannel(ch)) bucket.expired += 1;
          else bucket.manual += 1;
        }
        setArchivedCounts(counts);
      } catch (e) {
        /* best-effort; archive header falls back to zero */
      }
    };

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(recompute, 800);
    };

    recompute();

    const events = [
      "channel.updated",
      "channel.hidden",
      "channel.visible",
      "notification.added_to_channel",
    ];
    const subs = events.map((e) => chatClient.on(e, schedule));

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      subs.forEach((s) => s?.unsubscribe?.());
    };
  }, [chatClient, userId, isChatReady, laneOf, listRefreshKey]);

  const filters = useMemo(() => {
    const baseFilters = {
      type: "messaging",
      members: { $in: userId ? [userId] : [] },
      archived: false, // Only show non-archived channels
    };

    if (sidebarSearchQuery.trim()) {
      baseFilters.name = { $autocomplete: sidebarSearchQuery.trim() };
    }

    return baseFilters;
  }, [userId, sidebarSearchQuery]);

  // T031 (symptom 3): shared `last_updated` sort — see INBOX_CHANNEL_SORT
  // above. Module-level constant, already stable across renders.
  const sort = INBOX_CHANNEL_SORT;

  const options = useMemo(
    () => ({
      state: true,
      limit: 20,
    }),
    []
  );

  // Archive section per tab (design 1d): same query as the main list but for
  // Stream-archived channels. The render filter (below) scopes it to the tab.
  const archivedFilters = useMemo(() => {
    const f = {
      type: "messaging",
      members: { $in: userId ? [userId] : [] },
      archived: true,
    };
    if (sidebarSearchQuery.trim()) {
      f.name = { $autocomplete: sidebarSearchQuery.trim() };
    }
    return f;
  }, [userId, sidebarSearchQuery]);

  // Client-side render filter for <ChannelList channelRenderFilterFn>: keep only
  // the channels that classify to the active LANE, and — while a search query is
  // active and a non-"all" scope chip is picked (4b) — only those whose
  // counterpart matches the scope. Re-created per lane/scope so the list
  // re-filters instantly without a new server query; the resting (unsearched)
  // list is untouched.
  const listChannelFilterFn = useMemo(() => {
    const tab = activeTab;
    const scoping =
      Boolean(sidebarSearchQuery.trim()) &&
      searchScope &&
      searchScope !== "all";
    // D11: the intent filter only applies in the DM lane and only when narrowed.
    const intentActive = tab === LANE.DM && intentFilter && intentFilter !== "all";
    return (channels) =>
      channels.filter((ch) => {
        if (laneOf(ch) !== tab) return false;
        if (scoping && counterpartType(ch, userId) !== searchScope) return false;
        if (intentActive && deriveChannelIntent(ch?.data) !== intentFilter) {
          return false;
        }
        return true;
      });
  }, [activeTab, searchScope, sidebarSearchQuery, userId, laneOf, intentFilter]);

  // D24: the archive list is split into two species (frame 5i). Both share the
  // lane render filter; MANUAL keeps the writable (non-expired) rows, EXPIRED
  // keeps the read-only timer-expired rows. Derived from listChannelFilterFn so
  // the lane/scope logic stays in one place.
  const archiveManualFilterFn = useMemo(
    () => (channels) =>
      listChannelFilterFn(channels).filter((ch) => !isExpiredChannel(ch)),
    [listChannelFilterFn],
  );
  const archiveExpiredFilterFn = useMemo(
    () => (channels) =>
      listChannelFilterFn(channels).filter((ch) => isExpiredChannel(ch)),
    [listChannelFilterFn],
  );

  // D11: leaving the DM lane clears the intent narrowing so it can't silently
  // scope a lane it doesn't belong to.
  useEffect(() => {
    if (activeTab !== LANE.DM) setIntentFilter("all");
  }, [activeTab]);

  // Per-lane pending message-request counts (D29): the tab number = channel
  // unread PLUS this lane's pending cold-contact requests. Fed by the Requests
  // gate via setPendingRequestsByLane; the lane a request files under is its
  // origin (MESSAGE_REQUEST_ORIGIN_TO_LANE).
  const inboxTabCounts = useMemo(() => {
    const merged = { ...tabCounts };
    for (const [lane, n] of Object.entries(pendingRequestsByLane)) {
      if (n) merged[lane] = (merged[lane] || 0) + n;
    }
    return merged;
  }, [tabCounts, pendingRequestsByLane]);

  const handleSidebarSearch = useCallback((event) => {
    setSidebarSearchQuery(event.target.value);
  }, []);

  const handleSidebarSearchKeyDown = useCallback((event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }, []);

  const goToMeetings = useCallback(() => {
    navigate("/meetings");
  }, [navigate]);

  const declineIncomingCall = useCallback(() => {
    // The decliner stamps `declined` onto the call_invitation it received. It
    // never sent that message, so it uses the {message_id, channel_cid} we
    // captured from the message.new event. Best-effort — the modal always
    // closes even if the stamp can't fire (missing handle or network error).
    if (incomingCall?.message_id && incomingCall?.channel_cid) {
      Promise.resolve(
        chatCallOutcomeService.stampOutcome({
          message_id: incomingCall.message_id,
          channel_cid: incomingCall.channel_cid,
          outcome: "declined",
        })
      ).catch((e) => console.error("Decline stamp failed:", e));
    } else {
      console.warn("Declined call had no message handle to stamp");
    }
    setShowIncomingCallModal(false);
    setIncomingCall(null);
  }, [incomingCall]);

  const acceptIncomingCall = useCallback(() => {
    if (!incomingCall) return;
    handleIncomingCall(incomingCall);
    setShowIncomingCallModal(false);
  }, [handleIncomingCall, incomingCall]);

  const isLoading = !chatClient || !chatClient.userID || !isChatReady;

  return {
    chatClient,
    videoClient,
    userId,
    activeChannel,
    setActiveChannel,
    sidebarSearchQuery,
    setSidebarSearchQuery,
    filters,
    archivedFilters,
    sort,
    options,
    activeTab,
    setActiveTab,
    tabCounts: inboxTabCounts,
    tabTotals,
    archivedCounts,
    searchScope,
    setSearchScope,
    setPendingRequestsCount,
    setPendingRequestsByLane,
    listChannelFilterFn,
    // D24 archive: species-split render filters + row actions + refresh key.
    archiveManualFilterFn,
    archiveExpiredFilterFn,
    archiveChat,
    unarchiveChat,
    listRefreshKey,
    // D11: DM-tab intent filter.
    intentFilter,
    setIntentFilter,
    dmIntentCounts,
    // Perspective-specific inbox model (D1/D20): tabs, showTabBar, capabilities.
    inboxModel,
    openThreadByCid,
    handleSidebarSearch,
    handleSidebarSearchKeyDown,
    isLoading,
    handleStartCall,
    handleEndCall,
    goToMeetings,
    incomingCall,
    showIncomingCallModal,
    acceptIncomingCall,
    declineIncomingCall,
    isInCall,
    call,
    callType,
  };
};

export default useInbox;
